import React, { useState, useEffect, useCallback, useContext } from 'react';
import { ChevronRight, Loader, AlertCircle } from 'lucide-react';
import metaApi from '../../api/meta';
import { toast } from 'react-hot-toast';
import MetaDropdowns from '../../components/adsConnections/account/MetaDropdowns';
import { AuthContext } from '../../components/context/AuthContext';
import MetaAssetsLoadingState from './MetaAssetsLoadingState';

/** Graph API usa `act_<id>`; la lista de /meta-auth/accounts expone `account_id` numérico. */
function normalizeMetaAdAccountId(value) {
  if (value == null || value === '') return '';
  return String(value).replace(/^act_/i, '');
}

const MetaAssetsSelectionStep = ({
  activeMetaAccount,
  onComplete,
  onBack,
  onSkip: _onSkip,
  platform = 'meta',
}) => {
  const { auth, updateContextUser } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const [accounts, setAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [selectedPage, setSelectedPage] = useState(null);
  const [selectedInstagram, setSelectedInstagram] = useState(null);
  const [selectedWhatsAppPhone, setSelectedWhatsAppPhone] = useState(null);
  const isWhatsAppFlow = platform === 'whatsapp';

  const getWhatsappPhones = useCallback((account) => (
    (account?.whatsapp_business_accounts || []).flatMap((wa) => wa.phone_numbers || [])
  ), []);

  const applyAccountDefaults = useCallback((account, { preferSaved = false } = {}) => {
    if (!account) {
      setSelectedPage(null);
      setSelectedInstagram(null);
      setSelectedWhatsAppPhone(null);
      return;
    }

    const pages = account.pages || [];
    const instagramAccounts = account.instagram_accounts || [];
    const whatsappPhones = getWhatsappPhones(account);

    const savedPageId = auth?.user?.meta_business_page_id;
    const savedInstagramId = auth?.user?.meta_instagram_account_id;
    const savedWhatsappPhone = auth?.user?.meta_whatsapp_business_account_phone_number;

    let nextPage = null;
    let nextInstagram = null;
    let nextWhatsappPhone = null;

    if (preferSaved && savedPageId) {
      nextPage = pages.find((page) => String(page.id) === String(savedPageId)) || null;
    }
    if (!nextPage && pages.length === 1) {
      nextPage = pages[0];
    }

    if (preferSaved && savedInstagramId) {
      nextInstagram = instagramAccounts.find((instagram) => String(instagram.id) === String(savedInstagramId)) || null;
    }
    if (!nextInstagram && instagramAccounts.length === 1) {
      nextInstagram = instagramAccounts[0];
    }

    if (preferSaved && savedWhatsappPhone) {
      nextWhatsappPhone = whatsappPhones.find((phone) => String(phone) === String(savedWhatsappPhone)) || null;
    }
    if (!nextWhatsappPhone && whatsappPhones.length === 1) {
      nextWhatsappPhone = whatsappPhones[0];
    }

    setSelectedPage(nextPage);
    setSelectedInstagram(nextInstagram);
    setSelectedWhatsAppPhone(nextWhatsappPhone);
  }, [
    auth?.user?.meta_business_page_id,
    auth?.user?.meta_instagram_account_id,
    auth?.user?.meta_whatsapp_business_account_phone_number,
    getWhatsappPhones,
  ]);

  useEffect(() => {
    const fetchAccountWithAssets = async () => {
      try {
        setLoading(true);
        setError(null);

        if (!activeMetaAccount) {
          setError('No hay cuenta de Meta Ads seleccionada en Conexiones.');
          setLoading(false);
          return;
        }

        // Fetch all accounts with extended scopes to get assets
        const response = await metaApi.fetchMetaAccounts(true);
        const fetchedAccounts = response.data?.accounts || [];

        if (!fetchedAccounts || fetchedAccounts.length === 0) {
          setError('No se encontraron cuentas de Meta.');
          setLoading(false);
          return;
        }

        setAccounts(fetchedAccounts);

        // Cuenta activa viene de /meta/active-account con `id` (act_…), no siempre con `account_id`
        const activeId = normalizeMetaAdAccountId(
          activeMetaAccount.account_id ?? activeMetaAccount.id
        );
        const matchingAccount = fetchedAccounts.find(
          (acc) => normalizeMetaAdAccountId(acc.account_id) === activeId
        );

        if (matchingAccount) {
          setSelectedAccount(matchingAccount);
          applyAccountDefaults(matchingAccount, { preferSaved: true });
        } else {
          const label = activeId || activeMetaAccount.account_id || activeMetaAccount.id || '—';
          setError(`No se encontró la cuenta ${label} en los activos de Meta.`);
        }
      } catch (err) {
        setError('Error al cargar los activos de Meta. Por favor, intenta de nuevo.');
        console.error('Error fetching accounts with assets:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchAccountWithAssets();
  }, [activeMetaAccount, applyAccountDefaults]);

  const handleSave = async () => {
    if (!selectedAccount) {
      toast.error('Por favor selecciona una cuenta');
      return;
    }

    if (isWhatsAppFlow) {
      if (selectedAccount.pages?.length > 0 && !selectedPage) {
        toast.error('Por favor selecciona una página de Facebook');
        return;
      }
      if (selectedAccount.instagram_accounts?.length > 0 && !selectedInstagram) {
        toast.error('Por favor selecciona una cuenta de Instagram');
        return;
      }
      if (!selectedWhatsAppPhone) {
        toast.error('Por favor selecciona una cuenta de WhatsApp');
        return;
      }
    }

    setSaving(true);
    try {
      const payload = {
        accountId: selectedAccount.account_id,
      };

      if (isWhatsAppFlow) {
        payload.whatsappPhoneNumber = selectedWhatsAppPhone;
        payload.pageId = selectedPage?.id;
        payload.instagramId = selectedInstagram?.id;
      } else {
        payload.pageId = selectedPage?.id;
        payload.instagramId = selectedInstagram?.id;
      }

      const updatedUser = await metaApi.updateMetaConnectionData(payload);
      if (updatedUser) {
        updateContextUser(updatedUser);
      }

      toast.success(isWhatsAppFlow ? 'WhatsApp configurado exitosamente' : 'Configuración de Meta guardada exitosamente');
      onComplete();
    } catch (err) {
      console.error('Error saving meta connection:', err);
      toast.error('Error al guardar la configuración. Por favor, intenta de nuevo.');
    } finally {
      setSaving(false);
    }
  };

  const whatsAppNeedsPage =
    isWhatsAppFlow && (selectedAccount?.pages?.length > 0) && !selectedPage;
  const whatsAppNeedsInstagram =
    isWhatsAppFlow && (selectedAccount?.instagram_accounts?.length > 0) && !selectedInstagram;
  const continueDisabled =
    !selectedAccount ||
    saving ||
    loading ||
    (isWhatsAppFlow && !selectedWhatsAppPhone) ||
    whatsAppNeedsPage ||
    whatsAppNeedsInstagram;
  const selectedAccountWhatsappPhones = getWhatsappPhones(selectedAccount);

  if (loading) {
    return <MetaAssetsLoadingState platform={platform} />;
  }

  return (
    <div className="w-full min-h-[40vh] lg:min-h-[46vh] flex items-start lg:items-center animate-in fade-in duration-300">
      <div className="w-full rounded-[28px] bg-slate-950/90 border border-white/20 p-4 sm:p-6 lg:p-8 text-white shadow-2xl backdrop-blur-md">
        <div className="mx-auto w-full max-w-3xl">
          {/* Header */}
          <div className="mb-6 lg:mb-8 text-center">
            <h1 className="text-2xl lg:text-3xl font-bold mb-2">
              {isWhatsAppFlow ? 'Configurar WhatsApp' : 'Configurar Activos de Meta'}
            </h1>
            <p className="text-white/65 text-sm lg:text-base">
              {isWhatsAppFlow
                ? 'Seleccioná la página de Facebook, la cuenta de Instagram y el número de WhatsApp Business para tus campañas.'
                : 'Selecciona la página de Facebook y la cuenta de Instagram para tus campañas de Meta Ads.'}
            </p>
          </div>

          {/* Content */}
          {error ? (
            <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 mb-6">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="text-red-300 font-medium">Error al cargar cuentas</h3>
                  <p className="text-red-300/80 text-sm mt-1">{error}</p>
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-white/5 border border-white/20 rounded-2xl p-4 sm:p-5 lg:p-6 mb-6">
              <MetaDropdowns
                accounts={accounts}
                selectedAccountId={selectedAccount?.account_id}
                selectedFacebookPageId={selectedPage?.id}
                selectedInstagramId={selectedInstagram?.id}
                selectedWhatsappPhone={selectedWhatsAppPhone}
                onAccountSelect={(account) => {
                  setSelectedAccount(account);
                  applyAccountDefaults(account);
                }}
                onFacebookPageSelect={(page) => setSelectedPage(page)}
                onInstagramSelect={(instagram) => setSelectedInstagram(instagram)}
                onWhatsappSelect={(phone) => setSelectedWhatsAppPhone(phone)}
                disabled={saving || loading}
                showOnlyAccount={false}
                showFacebookPage
                showInstagram
                showWhatsapp={isWhatsAppFlow}
                facebookPageRequired={isWhatsAppFlow}
                instagramRequired={isWhatsAppFlow}
                whatsappRequired={isWhatsAppFlow}
                lockAccountSelection
              />
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3">
            <button
              onClick={onBack}
              disabled={saving}
              className="flex-1 px-4 py-3 bg-white/10 hover:bg-white/15 text-white font-medium rounded-lg transition-all border border-white/20 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Atrás
            </button>

            <button
              onClick={handleSave}
              disabled={continueDisabled}
              className="flex-1 px-4 py-3 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-medium rounded-lg transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl active:scale-95"
            >
              {saving ? (
                <>
                  <Loader className="w-4 h-4 animate-spin" />
                  Guardando...
                </>
              ) : (
                <>
                  Continuar
                  <ChevronRight className="w-4 h-4" />
                </>
              )}
            </button>
          </div>

          {/* Info */}
          {!selectedAccount?.pages?.length && selectedAccount && (
            <div className="bg-orange-500/10 border border-orange-500/30 rounded-lg p-4 mt-4">
              <p className="text-orange-300 text-sm">
                No se encontraron páginas de Facebook. Verifica que tu cuenta tiene acceso a las páginas en Meta Business Suite.
              </p>
            </div>
          )}
          {!selectedAccount?.instagram_accounts?.length && selectedAccount && (
            <div className="bg-orange-500/10 border border-orange-500/30 rounded-lg p-4 mt-4">
              <p className="text-orange-300 text-sm">
                No se encontraron cuentas de Instagram. Verifica la vinculación en Meta Business Suite.
              </p>
            </div>
          )}
          {isWhatsAppFlow && selectedAccount && selectedAccountWhatsappPhones.length === 0 && (
            <div className="bg-orange-500/10 border border-orange-500/30 rounded-lg p-4 mt-4">
              <p className="text-orange-300 text-sm">
                No se encontraron números de WhatsApp Business en esta cuenta.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MetaAssetsSelectionStep;
